/** Pending-orders tab pane — flat list of limits/stops across accounts. */
import { Loader2, RefreshCw, Ban } from 'lucide-react';

export default function PendingPane({ data, accounts, isAr, onCancelOne, cancelingId, onCancelAll, cancelingAll, onRefresh }) {
  const rows = [];
  data.accounts?.forEach((accBlock) => {
    const accInfo = accounts.find((a) => a.id === accBlock.account_id);
    accBlock.orders?.forEach((o) => rows.push({ ...o, accountId: accBlock.account_id, accountLabel: accInfo?.label || accBlock.account_id }));
  });

  const parseType = (t) => {
    // MetaApi returns ORDER_TYPE_BUY_LIMIT / SELL_STOP etc.
    if (!t) return { text: '—', color: 'text-white/40' };
    const u = String(t).toUpperCase();
    const isBuy = u.includes('BUY');
    const label = u.replace('ORDER_TYPE_', '').replace('_', ' ');
    return { text: label, color: isBuy ? 'text-emerald-300 bg-emerald-500/15' : 'text-red-300 bg-red-500/15' };
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-white/50">
          {isAr ? `${rows.length} أمر معلّق (تحدَّث كل 5 ثوانٍ)` : `${rows.length} pending order(s) — auto-refresh 5s`}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onRefresh}
            className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 hover:text-white text-xs"
            data-testid="pending-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onCancelAll}
            disabled={cancelingAll || rows.length === 0}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 text-[11px] font-bold disabled:opacity-40"
            data-testid="cancel-all-pending"
          >
            {cancelingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
            {isAr ? 'إلغاء الكل' : 'Cancel all'}
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm">
          {isAr ? 'لا توجد أوامر معلّقة' : 'No pending orders'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {/* Column headers — matches the 12-col grid of each row below. */}
          <div
            className="grid grid-cols-12 items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-[10px] font-black uppercase tracking-wider text-white/50"
            data-testid="pending-header"
          >
            <div className="col-span-2">{isAr ? 'الحساب' : 'Account'}</div>
            <div className="col-span-2">{isAr ? 'الرمز' : 'Symbol'}</div>
            <div className="col-span-2">{isAr ? 'نوع الأمر' : 'Type'}</div>
            <div className="col-span-1 text-right">{isAr ? 'اللوت' : 'Lot'}</div>
            <div className="col-span-2 text-right">{isAr ? 'سعر الدخول' : 'Entry'}</div>
            <div className="col-span-2 text-right">TP · SL</div>
            <div className="col-span-1 text-right">{isAr ? 'إلغاء' : '—'}</div>
          </div>
          {rows.map((o) => {
            const tp = parseType(o.type);
            return (
              <div
                key={`${o.accountId}-${o.broker_id}`}
                className="grid grid-cols-12 items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] text-xs"
                data-testid={`pending-row-${o.broker_id}`}
              >
                <div className="col-span-2 min-w-0">
                  <div className="font-bold truncate">{o.accountLabel}</div>
                  <div className="text-[10px] text-white/40 font-mono truncate">#{o.broker_id}</div>
                </div>
                <div className="col-span-2 font-mono">
                  <span className="font-bold">{o.symbol}</span>
                </div>
                <div className="col-span-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${tp.color}`}>
                    {tp.text}
                  </span>
                </div>
                <div className="col-span-1 text-right font-mono">{Number(o.volume).toFixed(2)}</div>
                <div className="col-span-2 text-right font-mono text-[#d4af37]">
                  @{Number(o.open_price).toFixed(5)}
                </div>
                <div className="col-span-2 text-right text-[10px] text-white/50 font-mono">
                  {o.tp && <div>TP {Number(o.tp).toFixed(2)}</div>}
                  {o.sl && <div>SL {Number(o.sl).toFixed(2)}</div>}
                </div>
                <div className="col-span-1 flex justify-end">
                  <button
                    onClick={() => onCancelOne(o.accountId, o.broker_id)}
                    disabled={cancelingId === o.broker_id}
                    className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-40"
                    title={isAr ? 'إلغاء' : 'Cancel'}
                    data-testid={`cancel-pending-${o.broker_id}`}
                  >
                    {cancelingId === o.broker_id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Ban className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
